import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import WorkerSearchViewModel from '@/models/Workers/WorkerSearchViewModel';
import WorkerSearchOptionsResponse from '@/models/Workers/WorkerSearchOptionsResponse';
import LocationProfileSearchRequest from '@/models/Locations/LocationProfileSearchRequest';
import LocationProfileDto from '@/models/Locations/LocationProfileDto';
import DistanceViewModel from '@/models/Shared/DistanceViewModel';
import SkillDto from '@/models/Skills/SkillDto';
import QualificationDto from '@/models/Qualifications/QualificationDto';
import PositionDto from '@/models/Positions/PositionDto';

export interface IWorkerListState {
  loading: boolean;
  workers: WorkerProfileDto[];
  workerSearchViewModel: WorkerSearchViewModel;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'WorkerList', namespaced: true})
class WorkerList extends VuexModule implements IWorkerListState {
  
  public loading = true;
  public workers: WorkerProfileDto[] = [];  
  public workerSearchViewModel: WorkerSearchViewModel = new WorkerSearchViewModel(null, null, null, false);
  public loadingDialog = false;

  @Action
  async searchWorkers() {
    const request = new WorkerProfileSearchRequest();
    request.statuses.push(this.workerSearchViewModel.selectedStatus);
    request.isAgreedToMarketing = this.workerSearchViewModel.isAgreedToMarketing;
    request.workTypes = this.workerSearchViewModel.selectedWorkTypes;
    if (this.workerSearchViewModel.selectedLocation != null) {
      if (this.workerSearchViewModel.selectedDistance.value != 0) {
        request.distance = this.workerSearchViewModel.selectedDistance.value;
        request.latitude = this.workerSearchViewModel.selectedLocation.latitude;
        request.longitude = this.workerSearchViewModel.selectedLocation.longitude;
      }
    }

    //Branch
    if (this.workerSearchViewModel.selectedBranch != null) {
      request.branchId = this.workerSearchViewModel.selectedBranch.id;
    }

    //Desk
    if (this.workerSearchViewModel.selectedDesk != null) {
      request.deskId = this.workerSearchViewModel.selectedDesk.id;
    }

    //Positions
    if (this.workerSearchViewModel.selectedPositions.length !== 0) {
      request.positionCodes = this.workerSearchViewModel.selectedPositions.map(function(position: PositionDto) {
        return position.code;
      });
    }  

    //Skills
    request.skillMatchType = this.workerSearchViewModel.selectedSkillMatchType;
    if (this.workerSearchViewModel.selectedSkills.length !== 0) {
      request.skillCodes = this.workerSearchViewModel.selectedSkills.map(function(skill: SkillDto) {
        return skill.code;
      });
    }

    //Qualifications
    request.qualificationMatchType = this.workerSearchViewModel.selectedQualificationMatchType;
    if (this.workerSearchViewModel.selectedQualifications.length !== 0) {
      request.qualificationCodes = this.workerSearchViewModel.selectedQualifications.map(function(qualification: QualificationDto) {
        return qualification.code;
      });
    }
    
    //Drivers
    if (this.workerSearchViewModel.driversOnly){
      request.driversOnly = true;
      request.minimumPassengers = this.workerSearchViewModel.selectedMinimumPassengers;
    }

    //Availability
    if (this.workerSearchViewModel.includeAvailability) {
      request.availability = this.workerSearchViewModel.selectedAvailabilityCodes.map(function(availabilityViewModel) {
        return availabilityViewModel.key;
      })
    } 

    //Location Restrictions
    if (this.workerSearchViewModel.selectedLocation) {
      request.locationId = this.workerSearchViewModel.selectedLocation.id;
      request.isFavouritesOnly = this.workerSearchViewModel.isFavouritesOnly;
      request.isIncludeBlocked = this.workerSearchViewModel.isIncludeBlocked;
    }

    this.setLoading(true);
    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setWorkers", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async getWorkerSearchOptions() {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/workers/search-options`)
    .then(response => {
      this.context.commit("setWorkerSearchOptions", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getLocationsForSearchOptions() {
    const request = new LocationProfileSearchRequest();
    
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/locations/search`, request)
    .then(response => {
      this.context.commit("setLocations", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = true;
    this.workers = [];
    this.workerSearchViewModel = new WorkerSearchViewModel(null, null, null, false);
    this.loadingDialog = false;
   
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLocations(locations: LocationProfileDto[]) {
      this.workerSearchViewModel.locations = locations;
  }

  @Mutation
  private async setWorkers(workers: WorkerProfileDto[]) {
      this.workers = workers;
  }

  @Mutation
  private async setWorkerSearchOptions(options: WorkerSearchOptionsResponse) {
    this.workerSearchViewModel = new WorkerSearchViewModel(options.branches, options.positions,  new DistanceViewModel(0, "Unlimited"), false);
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }
}

export const WorkerListModule = getModule(WorkerList)
